import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Reusable marketing empty state — icon, title, hints, and optional CTAs.
 * `i18nPrefix` should expose: emptyEyebrow, emptyTitle, emptyDescription,
 * emptyHint1..3, emptyCta, and optionally emptySecondary.
 */
export default function MarketingEmptyState({
  iconSrc,
  i18nPrefix,
  titleId,
  primaryTo = '/join',
  secondaryTo,
  hintKeys = ['emptyHint1', 'emptyHint2', 'emptyHint3'],
  className = '',
}) {
  const { t } = useTranslation();
  const secondaryLabel = t(`${i18nPrefix}.emptySecondary`, { defaultValue: '' });

  return (
    <section
      className={`relative overflow-hidden rounded-[1.5rem] border border-hairline/50 bg-card-surface px-6 py-12 text-center shadow-saas md:px-12 md:py-16 ${className}`.trim()}
      aria-labelledby={titleId}
    >
      <div
        className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-sky-wash/50"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-20 -right-12 h-56 w-56 rounded-full bg-signal/5"
        aria-hidden
      />

      <div className="relative mx-auto max-w-lg">
        <img
          src={iconSrc}
          alt=""
          className="mx-auto mb-6 h-24 w-24 object-contain drop-shadow-[0_12px_28px_rgba(113,75,103,0.18)]"
          width={96}
          height={96}
          loading="lazy"
          decoding="async"
        />

        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-signal">
          {t(`${i18nPrefix}.emptyEyebrow`)}
        </p>
        <h2 id={titleId} className="type-display text-2xl md:text-3xl">
          {t(`${i18nPrefix}.emptyTitle`)}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-steel">
          {t(`${i18nPrefix}.emptyDescription`)}
        </p>

        <ul className="mt-6 flex flex-wrap justify-center gap-2 text-xs text-steel">
          {hintKeys.map((key) => (
            <li
              key={key}
              className="rounded-full border border-hairline/60 bg-linen-canvas/80 px-3 py-1.5"
            >
              {t(`${i18nPrefix}.${key}`)}
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to={primaryTo}
            className="inline-flex min-w-[12rem] items-center justify-center rounded-full bg-cta-gradient px-6 py-3 text-sm font-semibold text-white shadow-saas transition-opacity hover:opacity-95"
          >
            {t(`${i18nPrefix}.emptyCta`)}
          </Link>
          {secondaryTo && secondaryLabel ? (
            <Link
              to={secondaryTo}
              className="inline-flex min-w-[12rem] items-center justify-center rounded-full border border-hairline bg-card-surface px-6 py-3 text-sm font-semibold text-signal transition-colors hover:bg-sky-wash/40"
            >
              {secondaryLabel}
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
}
